// Mixed-unit counting: turning "1 Costco tub + 2 Sysco cases + 8 loose bottles
// + 2.5 gal" into one on-hand number, without losing how it was counted.
//
// The normalized total is what every existing consumer needs — ordering, PAR
// comparison, valuation, reports — and it stays on the count row exactly as
// before. The raw entries live alongside it so the next person can see that
// the 10.125 gal was a tub, two cases, eight bottles and an open tub, and can
// correct one of them without recounting the lot.
//
// The rule that matters most: an entry whose conversion is unresolved is NOT
// added as zero. It is excluded from the total and reported, because silently
// counting an unknown quantity as nothing understates inventory and
// overstates the order.
//
// Pure ESM. No Deno APIs, no I/O.

import { buildCountLadder } from "./packageLadder.mjs";
import { countUnitAnchor, sameCountUnitName } from "./packText.mjs";
import { normalizeUnit, convertToBase } from "../menu-recipe-costing/units.mjs";

const round6 = (n) => Math.round(n * 1e6) / 1e6;

// Number(null), Number('') and Number([]) are all 0, so a missing value would
// otherwise pass as a legitimate zero. Reject the empties before coercing.
const finiteNonNegative = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const isMeasurable = (norm) =>
  !!norm && (norm.kind === "mass" || norm.kind === "volume" || norm.kind === "count");

/**
 * Resolves one raw count entry into the item's count unit.
 *
 * Three shapes are supported, in order of specificity:
 *   1. The entry names a ladder rung (purchase_option_id + entry_kind) — the
 *      rung carries how much one of it is worth.
 *   2. The entry names a plain unit ("2.5 gal") — converted directly.
 *   3. Neither resolves — unresolved, with a reason. Never zero.
 *
 * @returns {{ status:'valid'|'unresolved', normalized:number|null,
 *             reason:string|null, rung:object|null, warnings:string[] }}
 */
export function resolveCountEntry(entry, { rungs, countUnit, frozen = false } = {}) {
  const warnings = [];
  const unresolved = (reason) => ({ status: "unresolved", normalized: null, reason, rung: null, warnings });

  if (!entry) return unresolved("empty_entry");

  const quantity = finiteNonNegative(entry.entered_quantity);
  if (quantity === null) return unresolved("bad_quantity");

  // A closed count is evidence about a shelf nobody can re-examine. Its
  // normalized quantities were computed against the packages as they were
  // understood at the time, and re-deriving them now would let a later invoice
  // — a corrected case size, a re-parsed pack description — silently rewrite
  // what a completed inventory was worth. So a frozen entry reports exactly
  // what was stored, and an entry stored without a resolution stays unresolved
  // rather than acquiring one retroactively.
  if (frozen) {
    const stored = finiteNonNegative(entry.normalized_quantity);
    if (entry.conversion_status !== "unresolved" && stored !== null) {
      return { status: "valid", normalized: stored, reason: null, rung: null, warnings, frozen: true };
    }
    return unresolved(entry.conversion_reason || "unresolved_package");
  }

  // The item's own shelf unit. A wrapper or piece noun (bottle, case, bag)
  // anchors a count perfectly well — it just cannot be CONVERTED into, which
  // the plain-measure branch below enforces. Refusing it here instead is what
  // made every entry on a bottle-counted item unresolvable, ladder rungs
  // included, while the identical item in `each` resolved fine.
  const countAnchor = countUnitAnchor(countUnit);
  if (!countAnchor.anchorable) return unresolved("unknown_count_unit");
  const countNorm = normalizeUnit(countUnit);

  // 1. A ladder rung — a package or inner pack of a specific vendor product.
  const optionId = entry.purchase_option_id ?? null;
  // A package-kind entry whose option row was deleted (the FK is ON DELETE
  // SET NULL) is NOT a plain measure: "2 cases" must never be silently
  // re-read as "2 fl oz" just because the case's definition went away.
  if (
    optionId == null &&
    (entry.entry_kind === "full_package" ||
      entry.entry_kind === "loose_inner_pack" ||
      entry.entry_kind === "partial_package")
  ) {
    return unresolved("unresolved_package");
  }
  if (optionId != null) {
    const wanted = entry.entry_kind === "loose_inner_pack" ? "inner_pack" : "package";
    const rung = (rungs || []).find(
      (r) => r.optionId != null && String(r.optionId) === String(optionId) && r.level === wanted,
    );
    if (!rung) return unresolved("unresolved_package");
    if (!rung.allowsFractional && !Number.isInteger(quantity)) {
      warnings.push(
        `${rung.label} is counted in whole ${rung.unitLabel}s; ${quantity} was rounded down to ${Math.floor(quantity)}.`,
      );
      const whole = Math.floor(quantity);
      return { status: "valid", normalized: round6(whole * rung.qtyInCountUnit), reason: null, rung, warnings };
    }
    return { status: "valid", normalized: round6(quantity * rung.qtyInCountUnit), reason: null, rung, warnings };
  }

  // 2. A plain measure in some compatible unit.
  const enteredUnit = entry.entered_unit ?? countUnit;
  const enteredNorm = normalizeUnit(enteredUnit);

  // Identity first, and by NAME: "8 bottles" on a bottle-counted item is
  // eight, whether or not the engine can convert the word. This is the only
  // thing a container-unit item may resolve without naming a vendor package —
  // a DIFFERENT wrapper ("2 cases" on a bottle shelf) still falls through to
  // the refusals below, because how many bottles are in a case is a question
  // about a specific product, not about the word.
  if (sameCountUnitName(enteredUnit, countUnit)) {
    return { status: "valid", normalized: round6(quantity), reason: null, rung: null, warnings };
  }
  if (!isMeasurable(enteredNorm)) return unresolved("unknown_entered_unit");
  // A measure cannot be poured into a wrapper: "2.5 gal" says nothing about
  // how many BOTTLES that is without a package to bridge through.
  if (countAnchor.kind === "container") return unresolved("incompatible_units");
  if (enteredNorm.key === countNorm.key) {
    return { status: "valid", normalized: round6(quantity), reason: null, rung: null, warnings };
  }
  const conv = convertToBase(quantity, enteredUnit, countNorm.base, {});
  warnings.push(...(conv.warnings || []));
  if (!conv.ok) return unresolved("incompatible_units");
  return { status: "valid", normalized: round6(conv.qty / countNorm.factor), reason: null, rung: null, warnings };
}

/**
 * Human-readable explanation for an unresolved entry.
 */
export const UNRESOLVED_REASONS = Object.freeze({
  empty_entry: "This entry is empty.",
  bad_quantity: "The quantity must be a number of zero or more.",
  unknown_count_unit: "The item's count unit is not one we can convert.",
  unresolved_package: "This vendor package has no confirmed size, so it cannot be added to the total.",
  unknown_entered_unit: "That unit is not one we can convert.",
  incompatible_units: "That unit cannot be converted into the item's count unit.",
});

/**
 * Normalizes a whole set of raw count entries into one on-hand total.
 *
 * @param {object} args
 * @param {Array}  args.entries    raw count_item_quantity_entries rows
 * @param {string} args.countUnit  the item's canonical count unit
 * @param {Array}  [args.options]  purchase_options rows for the item
 * @param {Object} [args.vendorNamesById]
 * @returns {{ total: number|null, countUnit: string|null,
 *             resolved: Array, unresolved: Array,
 *             hasUnresolved: boolean, warnings: string[] }}
 *   total is null — never 0 — when there are entries but none resolved, so a
 *   caller cannot mistake "we could not work it out" for "there is none".
 */
export function normalizeCountEntries({ entries, countUnit, options, vendorNamesById, frozen = false } = {}) {
  const { rungs } = buildCountLadder({ countUnit, options, vendorNamesById });
  const resolved = [];
  const unresolved = [];
  const warnings = [];

  (entries || []).forEach((entry, index) => {
    const result = resolveCountEntry(entry, { rungs, countUnit, frozen });
    warnings.push(...result.warnings);
    if (result.status === "valid") {
      resolved.push({
        ...entry,
        sort_order: entry?.sort_order ?? index,
        normalized_quantity: result.normalized,
        normalized_unit: normalizeUnit(countUnit).key,
        conversion_status: "valid",
        rung: result.rung,
      });
    } else {
      unresolved.push({
        ...entry,
        sort_order: entry?.sort_order ?? index,
        normalized_quantity: null,
        conversion_status: "unresolved",
        reason: result.reason,
        reasonText: UNRESOLVED_REASONS[result.reason] || "This entry could not be converted.",
      });
    }
  });

  const list = entries || [];
  let total = null;
  if (resolved.length > 0) {
    total = round6(resolved.reduce((sum, e) => sum + e.normalized_quantity, 0));
  } else if (list.length === 0) {
    // No entries at all is a legitimate zero-length list, not a failure. The
    // caller decides whether that means "uncounted" or "counted as none".
    total = null;
  }

  if (unresolved.length > 0) {
    warnings.push(
      `${unresolved.length} ${unresolved.length === 1 ? "entry is" : "entries are"} not included in the total because ${unresolved.length === 1 ? "its" : "their"} package size is unconfirmed.`,
    );
  }

  return {
    total,
    countUnit: normalizeUnit(countUnit).key || null,
    resolved,
    unresolved,
    hasUnresolved: unresolved.length > 0,
    warnings,
  };
}

/**
 * On-hand for an item counted in several locations: the sum of each location's
 * normalized total.
 *
 * A location whose entries are entirely unresolved contributes null, not zero,
 * and the whole item is flagged so a PAR comparison can be withheld rather
 * than computed against a number we know is short.
 */
export function combineLocationTotals(locationTotals) {
  const list = (locationTotals || []).filter((t) => t != null);
  // `t.total != null` first: Number(null) is 0, which is finite, so a null
  // total would otherwise slip in as a location that counted nothing — the
  // exact confusion this module exists to prevent.
  const numeric = list.filter((t) => t.total != null && Number.isFinite(Number(t.total)));
  if (numeric.length === 0) {
    return { total: null, partial: list.length > 0, locations: list.length };
  }
  return {
    total: round6(numeric.reduce((sum, t) => sum + Number(t.total), 0)),
    partial: numeric.length !== list.length,
    locations: list.length,
  };
}

/**
 * A short description of how a quantity was counted, e.g.
 * "1 case + 8 bottles + 2.5 gal". Shown beside the total so the number is
 * auditable at a glance.
 */
export function describeEntries(resolvedEntries) {
  return (resolvedEntries || [])
    .map((entry) => {
      const qty = Number(entry.entered_quantity);
      if (!Number.isFinite(qty)) return null;
      const unit = entry.rung?.unitLabel || entry.entered_unit || "";
      // Package nouns pluralize ("2 cases"); measure units do not ("2.5 gal",
      // never "2.5 gals").
      const isPackageNoun = entry.rung?.level === "package" || entry.rung?.level === "inner_pack";
      const label = isPackageNoun && qty !== 1 && unit && !unit.endsWith("s") ? `${unit}s` : unit;
      return `${round6(qty)} ${label}`.trim();
    })
    .filter(Boolean)
    .join(" + ");
}
